import * as React from "react";
import { Container, Stack } from "@mui/material";
import { isEqual } from "lodash";
import { useXoraiaValue } from "../../../hooks/useXoraia";
import { TUseXElement, useXElementValue } from "../../../hooks/useXElement";

import { generateSide } from "../../../lib/generateSide";
import { generateBorder } from "../../../lib/generateBorder";
import { generateRadius } from "../../../lib/generateRadius";
import { IXStackProps } from "../../../utils/interfaces/containers/XStack.interfaces";
import { TCoreXElement } from "../../../utils/types/core/CoreXelement";

const compareProps = (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
};

const XStackCore: TCoreXElement<IXStackProps> = React.memo(
  ({ props, DynamicXElement, devProps, children, index }) => {
    const { env } = useXoraiaValue();

    // WE ARE GETTING THE CURRENT ELEMENT FROM THE ATOM FAMILY
    const xElement = useXElementValue<TUseXElement<IXStackProps>>(index);

    const {
      containerMode = "NONE",
      direction,
      gap,
      backgroundImage,
      backgroundMode = "CONTAINED",
      backgroundAttachment = "scroll",
      backgroundPosition = "center",
      backgroundSize = "cover",
      bgColor,
      padding,
      margin,
      hoverBgColor,
      hoverBorder,
      boxShadow,
      border,
      borderRadius,
      ...sx
    } = props;

    let bgImg: any = {};

    if (backgroundImage) {
      bgImg = {
        backgroundImage: `url("${backgroundImage}")`,
        backgroundRepeat: "no-repeat",
        backgroundSize,
        backgroundAttachment,
        backgroundPosition,
      };
    }

    const filled = backgroundMode === "FILL";

    const elements =
      children ??
      (DynamicXElement
        ? xElement.elementsIndexes.map((index, position) => (
            <DynamicXElement
              key={index}
              child={null}
              hierachy={xElement.hierachy}
              index={index}
              position={position}
            />
          ))
        : []);

    const hasChildren = children ?? !!(elements as any).length;

    const stackProps: any = {
      direction,
      sx: {
        gridColumn: `span ${parseInt(props.colSpan?.toString() ?? "1")}`,
        gridRow: `span ${parseInt(props.rowSpan?.toString() ?? "1")}`,
        boxShadow: boxShadow,
        border: generateBorder(border),
        borderRadius: generateRadius(borderRadius),
        ...sx,
        ...(filled
          ? {}
          : { bgcolor: !!!hasChildren ? "rgba(220,220,220, 0.2)" : bgColor }),
        ...(!filled && (containerMode === "NONE" || !containerMode)
          ? bgImg
          : {}),
        padding: generateSide("padding", padding),
        margin: generateSide("margin", margin),
        "&:hover": {
          bgcolor: !filled ? hoverBgColor : undefined,
          hoverBorder: generateBorder(hoverBorder),
        },
      },

      ...{
        ...(env === "dev" && !filled && containerMode === "NONE" && devProps),
      },
    };

    if (gap) stackProps.spacing = gap;

    const placeholder = (
      <p
        style={{
          height: "100px",
          padding: "20px",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: 0,
        }}
      >
        Ajouter un element
      </p>
    );

    const elementOrPlaceholder = hasChildren ? elements : placeholder;

    let stack = <Stack {...stackProps}>{elementOrPlaceholder}</Stack>;

    if (containerMode === "CENTER") {
      stack = (
        <Container
          {...(env === "dev" && !filled && devProps)}
          sx={{
            gridColumn: `span ${parseInt(props.colSpan?.toString() ?? "1")}`,
            gridRow: `span ${parseInt(props.rowSpan?.toString() ?? "1")}`,
          }}
        >
          {backgroundImage && !filled ? (
            <Stack
              sx={{
                ...bgImg,
                width: sx.width || "100%",
                height: sx.height || "100%",
              }}
            >
              <Stack {...stackProps}>{elementOrPlaceholder}</Stack>
            </Stack>
          ) : (
            <Stack {...stackProps}>{elementOrPlaceholder}</Stack>
          )}
        </Container>
      );
    }

    if (containerMode === "FILL") {
      stack = (
        <Container
          {...(env === "dev" && !filled && devProps)}
          maxWidth={false}
          sx={{
            gridColumn: `span ${parseInt(props.colSpan?.toString() ?? "1")}`,
            gridRow: `span ${parseInt(props.rowSpan?.toString() ?? "1")}`,
          }}
        >
          {backgroundImage && !filled ? (
            <Stack
              sx={{
                ...bgImg,
                width: sx.width || "100%",
                height: sx.height || "100%",
              }}
            >
              <Stack {...stackProps}>{elementOrPlaceholder}</Stack>
            </Stack>
          ) : (
            <Stack {...stackProps}>{elementOrPlaceholder}</Stack>
          )}
        </Container>
      );
    }

    if (filled) {
      return (
        <Stack
          {...(env === "dev" && devProps)}
          height="max-content"
          width="100%"
          sx={{
            ...bgImg,
            gridColumn: `span ${parseInt(props.colSpan?.toString() ?? "1")}`,
            gridRow: `span ${parseInt(props.rowSpan?.toString() ?? "1")}`,
          }}
        >
          <Stack
            height="100%"
            width="100%"
            bgcolor={bgColor}
            sx={{
              "&:hover": {
                bgcolor: hoverBgColor,
              },
            }}
          >
            {containerMode === "NONE" || !containerMode ? (
              <Stack {...stackProps}>{elementOrPlaceholder}</Stack>
            ) : (
              stack
            )}
          </Stack>
        </Stack>
      );
    }

    return stack;
  },
  compareProps
);

export default XStackCore;
